/* eslint-disable no-bitwise */
import { JOB_ORDER_TIME_STATUSES } from '../../constants/jobOrderConstants';
import { FEED_BACK } from '../../theme/colorConstants';

const ACTIVE_PLACEMENT_STATUS = 'active';

const GENERIC_US_ZONE = {
  EST: 'ET',
  EDT: 'ET',
  CST: 'CT',
  CDT: 'CT',
  MST: 'MT',
  MDT: 'MT',
  PST: 'PT',
  PDT: 'PT',
};

export const CARD_VARIANTS = {
  NEEDS_ATTENTION: 'needsAttention',
  ALL_OPENINGS_FILLED: 'allOpeningsFilled',
  FULLY_STAFFED: 'fullyStaffed',
  JOB_COMPLETE: 'jobComplete',
  JOB_STARTED: 'jobStarted',
  CANCELLED: 'cancelled',
};

export const FULFILLMENT_TONES = {
  LOW: 'low',
  NEUTRAL: 'neutral',
  FULL: 'full',
};

export const CLOCK_TONES = {
  DISABLED: 'disabled',
  SHORT: 'short',
  MET: 'met',
};

const toCount = (value) => (typeof value === 'number' && !Number.isNaN(value) ? value : 0);

export const getPlacementsNeeded = (jobOrder) =>
  Math.max(0, toCount(jobOrder?.numOpenings) - toCount(jobOrder?.placementsCount));

export const getOverstaffStats = (jobOrder) => {
  const openings = toCount(jobOrder?.numOpenings);
  const overstaff = toCount(jobOrder?.numOverstaff);
  const placements = toCount(jobOrder?.placementsCount);
  const filledBeyondOpenings = Math.max(0, placements - openings);
  const filled = Math.min(filledBeyondOpenings, overstaff);
  const percent = overstaff > 0 ? Math.round((filled / overstaff) * 100) : 0;

  return {
    filled,
    total: overstaff,
    percent,
    available: Math.max(0, overstaff - filled),
    label: `${filled}/${overstaff} (${percent}%)`,
  };
};

export const getStaffingVariant = (jobOrder) => {
  const openings = toCount(jobOrder?.numOpenings);
  const overstaff = toCount(jobOrder?.numOverstaff);
  const placements = toCount(jobOrder?.placementsCount);

  if (placements < openings) {
    return CARD_VARIANTS.NEEDS_ATTENTION;
  }
  if (placements < openings + overstaff) {
    return CARD_VARIANTS.ALL_OPENINGS_FILLED;
  }
  return CARD_VARIANTS.FULLY_STAFFED;
};

export const getCardVariant = ({ jobOrder, timeStatus, isCancelled }) => {
  if (isCancelled) {
    return CARD_VARIANTS.CANCELLED;
  }
  if (timeStatus === JOB_ORDER_TIME_STATUSES.COMPLETED) {
    return CARD_VARIANTS.JOB_COMPLETE;
  }
  if (timeStatus === JOB_ORDER_TIME_STATUSES.ACTIVE) {
    return CARD_VARIANTS.JOB_STARTED;
  }
  return getStaffingVariant(jobOrder);
};

const toneForFill = (fill) => {
  const percent = Math.round(fill * 100);
  if (percent >= 100) {
    return FULFILLMENT_TONES.FULL;
  }
  if (percent < 75) {
    return FULFILLMENT_TONES.LOW;
  }
  return FULFILLMENT_TONES.NEUTRAL;
};

/**
 * Two-part fill bar. The first segment is regular openings, the second is
 * overstaff (OT) openings. Widths follow slot counts; each segment fills on its own.
 */
export const getFillBarSegments = (jobOrder) => {
  const openings = toCount(jobOrder?.numOpenings);
  const overstaff = toCount(jobOrder?.numOverstaff);
  const placements = toCount(jobOrder?.placementsCount);
  const openingsFilled = Math.min(placements, openings);
  const overstaffFilled = Math.min(Math.max(0, placements - openings), overstaff);
  const openingsFill = openings > 0 ? openingsFilled / openings : 0;

  const segments = [];
  if (openings > 0 || overstaff === 0) {
    segments.push({
      key: 'openings',
      weight: Math.max(openings, 1),
      fill: openingsFill,
      tone: toneForFill(openingsFill),
    });
  }

  if (overstaff > 0) {
    const overstaffFill = overstaffFilled / overstaff;
    segments.push({
      key: 'overstaff',
      weight: overstaff,
      fill: overstaffFill,
      tone: toneForFill(overstaffFill),
    });
  }

  return segments;
};

export const getFulfillmentPresentation = (fillRatio) => {
  const value = typeof fillRatio === 'number' && !Number.isNaN(fillRatio) ? fillRatio : 0;
  const percent = Math.round(value);
  let tone = FULFILLMENT_TONES.NEUTRAL;
  if (percent < 75) {
    tone = FULFILLMENT_TONES.LOW;
  } else if (percent >= 100) {
    tone = FULFILLMENT_TONES.FULL;
  }

  return {
    percent,
    barValue: Math.min(100, Math.max(0, percent)),
    tone,
  };
};

export const getReliabilityScore = (score) => {
  if (typeof score !== 'number' || Number.isNaN(score)) {
    return null;
  }
  const value = Math.round(score * 100);
  return {
    value,
    tone: value >= 90 ? 'high' : 'mid',
  };
};

// Amber sampled from the reliability ring: red holds, then this, then green.
const RELIABILITY_AMBER = '#EE8726';

// Color along the ring, as a percent of a full score. Low is red, high is green.
export const RELIABILITY_RING_STOPS = [
  { at: 0, color: FEED_BACK.RED_50 },
  { at: 42, color: RELIABILITY_AMBER },
  { at: 100, color: FEED_BACK.GREEN_70 },
];

const clampPercent = (percent) => Math.min(100, Math.max(0, percent));

const hexToRgb = (hex) => {
  const normalized = hex.replace('#', '');
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
    .join('')}`.toUpperCase();

export const getReliabilityRingColor = (percent) => {
  const value = clampPercent(typeof percent === 'number' && !Number.isNaN(percent) ? percent : 0);
  const upperIndex = RELIABILITY_RING_STOPS.findIndex((stop) => stop.at >= value);
  const upper =
    RELIABILITY_RING_STOPS[upperIndex] || RELIABILITY_RING_STOPS[RELIABILITY_RING_STOPS.length - 1];
  const lower = RELIABILITY_RING_STOPS[Math.max(0, upperIndex - 1)] || upper;
  if (upper.at === lower.at) {
    return upper.color;
  }
  const mix = (value - lower.at) / (upper.at - lower.at);
  const from = hexToRgb(lower.color);
  const to = hexToRgb(upper.color);
  return rgbToHex({
    r: from.r + (to.r - from.r) * mix,
    g: from.g + (to.g - from.g) * mix,
    b: from.b + (to.b - from.b) * mix,
  });
};

const placementPunches = (placement) => placement?.timecard?.current?.punches || [];

const hasClockIn = (placement) => placementPunches(placement).some((punch) => punch?.in?.stamp);

const hasClockedOut = (placement) => {
  const punches = placementPunches(placement);
  const clockedIn = punches.some((punch) => punch?.in?.stamp);
  return clockedIn && punches.every((punch) => !punch?.in?.stamp || punch?.out?.stamp);
};

export const getClockRatios = (jobOrder) => {
  const activePlacements = (jobOrder?.placements || []).filter(
    (placement) => placement?.state?.status === ACTIVE_PLACEMENT_STATUS
  );
  const clockedIn = activePlacements.filter(hasClockIn).length;
  const clockedOut = activePlacements.filter(hasClockedOut).length;

  return {
    clockedIn,
    clockedInDenominator: activePlacements.length,
    clockedOut,
    clockedOutDenominator: clockedIn,
  };
};

export const getClockTone = ({ numerator, denominator, enabled }) => {
  if (!enabled) {
    return CLOCK_TONES.DISABLED;
  }
  if (denominator > 0 && numerator < denominator) {
    return CLOCK_TONES.SHORT;
  }
  return CLOCK_TONES.MET;
};

export const clocksAreEnabled = ({ timeStatus, isCancelled }) =>
  !isCancelled && timeStatus !== JOB_ORDER_TIME_STATUSES.UPCOMING;

export const getFlipActionKey = ({ isFlipped, isCancelled, timeStatus }) => {
  if (isCancelled) {
    return null;
  }
  if (isFlipped) {
    return 'jobOrder.viewJob';
  }
  if (timeStatus === JOB_ORDER_TIME_STATUSES.COMPLETED) {
    return 'jobOrder.finalizeTimeCard';
  }
  if (timeStatus === JOB_ORDER_TIME_STATUSES.ACTIVE) {
    return 'jobOrder.startCheckInAction';
  }
  return 'jobOrder.viewRoster';
};

export const getPrimaryActionKey = (timeStatus) =>
  timeStatus === JOB_ORDER_TIME_STATUSES.COMPLETED
    ? 'jobOrder.postShiftReportDetails'
    : 'jobOrder.manageRoster';

const isValidTimeZone = (timeZone) => {
  if (typeof timeZone !== 'string' || !timeZone) {
    return false;
  }
  try {
    Intl.DateTimeFormat('en-US', { timeZone }).format();
    return true;
  } catch {
    return false;
  }
};

const formatZonedTime = (epoch, timeZone) => {
  if (epoch == null || epoch === '') {
    return '';
  }
  const parsed = parseInt(epoch, 10);
  if (Number.isNaN(parsed)) {
    return '';
  }

  const options = { hour: '2-digit', minute: '2-digit', hour12: true };
  if (timeZone) {
    options.timeZone = timeZone;
  }

  const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(new Date(parsed));
  const hour = (parts.find((part) => part.type === 'hour')?.value || '').padStart(2, '0');
  const minute = parts.find((part) => part.type === 'minute')?.value || '00';
  const dayPeriod = (parts.find((part) => part.type === 'dayPeriod')?.value || '').toUpperCase();
  if (!hour || !dayPeriod) {
    return '';
  }
  return `${hour}:${minute} ${dayPeriod}`;
};

const zoneAbbreviation = (epoch, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short',
  }).formatToParts(new Date(epoch));
  const raw = parts.find((part) => part.type === 'timeZoneName')?.value || '';
  return GENERIC_US_ZONE[raw] || raw;
};

export const formatShiftRange = (start, end, timeZone) => {
  const zone = isValidTimeZone(timeZone) ? timeZone : undefined;
  const startLabel = formatZonedTime(start, zone);
  const endLabel = formatZonedTime(end, zone);
  if (!startLabel || !endLabel) {
    return '';
  }
  if (!zone) {
    return `${startLabel} - ${endLabel}`;
  }
  const parsedStart = parseInt(start, 10);
  const abbrev = zoneAbbreviation(parsedStart, zone);
  return abbrev ? `${startLabel} - ${endLabel} ${abbrev}` : `${startLabel} - ${endLabel}`;
};

export const formatJobDate = (epoch, timeZone) => {
  if (epoch == null || epoch === '') {
    return '';
  }
  const parsed = parseInt(epoch, 10);
  if (Number.isNaN(parsed)) {
    return '';
  }
  const zone = isValidTimeZone(timeZone) ? timeZone : undefined;
  return new Date(parsed).toLocaleDateString('en-US', zone ? { timeZone: zone } : undefined);
};

export const formatJobAddress = (location) => {
  if (!location) {
    return '';
  }
  const stateZip = [location.state, location.zipcode].filter(Boolean).join(' ');
  return [location.address, location.city, stateZip].filter(Boolean).join(', ');
};
